/**
 * @file Creates a WindowView to display content
 * @author Matt Glynn & Mike Naughton
 * @version 1.0.0
 */
import Backbone from "backbone";
import _ from "underscore";
import $ from "jquery";
import PanelHeadingView from "core/views/PanelHeadingView";
import page from "core/Page";
var WindowView = Backbone.View.extend(
  {
    /**
     * @augments Backbone.View
     * @constructor
     * @name WindowView
     */
    className: "window well panel panel-default form-group",

    template: _.template(
      '<div class="panel-body"></div><%if(footer){%><div class="panel-footer"></div><%}%>'
    ),

    events: {
      /**
       * Click the size control button
       */
      "click .size-control": function () {
        if (this._maximized) {
          this.restore();
        } else {
          this.maximize();
        }
      },
      /**
       * Set the BaseMap
       * */
      "change .basemap-type": function () {
        if (this.mapView) {
          this.mapView.setBaseMap(this.$(".basemap-type").val());
        }
      },
      /**
       * Click the close button to close the window
       */
      "click .panel-heading .close": function () {
        this.trigger("close");

        if (!this.options.hideOnClose) {
          // COMPLETELY UNBIND THE VIEW
          this.undelegateEvents();
          this.$el.removeData().unbind();
          // Remove view from DOM
          this.remove();
          Backbone.View.prototype.remove.call(this);
        } else {
          this.hide(); //don't remove - client might want to keep us around
        }
      },
      /**
       * Move the window using JQuerys draggable functionality
       */
      drag: function () {
        //	check the maximized state; If it is maximized disable restore functionality and enable maximize
        if (this._maximized) {
          this._clearRestore();
        }
      },
    },
    /**
     * Initializes a WindowView
     * @constructor
     * @param {Object} options - Instantiation and display options for the windowView
     * @param {boolean} options.allowMaximize - Allow the window to be maximized via the maximize button
     * @param {boolean} options.allowResize - Allow The window to be resized via the resizeable handles
     * @param {boolean} options.allowMove - Allow The window to be moved
     * @param {boolean} options.maximized - Sets if the window should be defaulted to maximized
     * @param {boolean} options.footer -  Allow The window to have a footer
     * @param {boolean} options.footer -  Allow The window to have a header
     * @param {boolean} options.title -  Set the title of a window
     * @param {boolean} options.title -  Set the title of a window
     */
    initialize: function (options) {
      //set the following initialization properties into the options object for the window
      _.bindAll(
        this,
        "render",
        "_initResizable",
        "_initMovable",
        "hide",
        "show",
        "toggle",
        "center",
        "position",
        "size",
        "setContent",
        "setHeader",
        "setFooter",
        "getContent",
        "getFooter",
        "maximize",
        "restore",
        "_clearRestore",
        "remove",
        "_containerDimensions",
        "_resetDimensions",
        "setShadow"
      ); // fixes loss of context for 'this' within methods

      var self = this;
      //The default z-Index should be above the map and not auto
      this.options = _.defaults({}, options, {
        allowMaximize: true,
        allowResize: true,
        allowMove: true,
        allowClose: true,
        hideOnClose: false,
        maximized: false,
        footer: false,
        header: true,
        shadow: false,
        title: "",
        "z-index": 2,
        anchor: false,
      });

      this.header = new PanelHeadingView({
        title: this.options.title,
      });

      if (_.isObject(this.options.anchor)) {
        this._anchor = _.defaults({}, this.options.anchor, {
          left: false,
          center: false,
          right: false,
          top: false,
          bottom: false,
          container: null,
        });
      }

      this.setShadow(this.options.shadow);
      this.setZIndex(this.options["z-index"]);

      this.$el.hide();
      this._visible = false;

      //Bind the WindowView resizing function for when the browser window is resized
      $(window).bind("resize", function (event) {
        if (!$(event.target).hasClass("ui-resizable")) {
          self._resetDimensions();
        }
      });

      this._maximized = false;

      if (this.options.maximized) {
        this.maximize();
      }

      if (!this.options.maximized) {
        if (this.options.position) {
          this.position(this.options.position);
        } else {
          this.position(this.position());
        }
        this._resetDimensions(); //Position the window to the correct edge using the container
      }
    },
    /**
     * Initializes the window to be resizeable
     */
    _initResizable: function () {
      var handles;

      if (this._anchor) {
        handles = [];
        var xHandle;
        var yHandle;

        if (!this._anchor.left) {
          handles.push("w");
          xHandle = "w";
        }

        if (!this._anchor.right) {
          handles.push("e");
          xHandle = "e";
        }

        if (!this._anchor.top) {
          handles.push("n");
          yHandle = "n";
        }

        if (!this._anchor.bottom) {
          handles.push("s");
          yHandle = "s";
        }

        if (xHandle && yHandle) {
          handles.push(yHandle + xHandle);
        }
        handles = handles.join(",");
      }

      this.$el.resizable({
        //Set the resizeable property on the containing inline block div first
        maxHeight: WindowView.MAX_HEIGHT,
        maxWidth: WindowView.MAX_WIDTH,
        minHeight: WindowView.MIN_HEIGHT,
        minWidth: WindowView.MIN_WIDTH,
        handles: handles,
        resize: _.throttle(
          _.bind(function () {
            this._recalculateContentSize();
          }, this),
          250
        ),
      });
    },
    /**
     * Initializes the window to be movable
     */
    _initMovable: function () {
      var axis = false;
      var movable = true; //if we're anchored in too many places, we shouldn't allow movement

      if (this._anchor) {
        var lockX = false,
          lockY = false;

        if (this._anchor.top || this._anchor.bottom) {
          lockY = true;
        }

        if (this._anchor.left || this._anchor.right) {
          lockX = true;
        }

        if (lockY && lockX) {
          movable = false;
        } else if (lockX) {
          axis = "y";
        } else if (lockY) {
          axis = "x";
        }
      }

      if (movable) {
        //Set the draggable property on the outer div
        this.$el.draggable({
          cursor: "move",
          handle: ".panel-heading",
          axis: axis,
          containment: $(".page-content").parent(),
        });
      }

      this._movable = movable;
    },
    /**
     * Renders the visual properties of the WindowView onto the document
     */
    render: function () {
      this.$el.html(this.template(this.options));

      if (this.options.header) {
        this.header.render();
        this.$("> .panel-heading").remove();
        this.$("> .panel-body").before(this.header.$el);
      }

      if (this.options.allowClose) {
        this.header
          .getRightControlBar()
          .append(
            '<button class="close window-keep"><span>&times;</span></button>'
          );
      }

      if (this.options.allowMaximize) {
        this.header
          .getRightControlBar()
          .append(
            '<span class="window-keep glyphicon glyphicon-resize-full size-control btn btn-default"></span>'
          );
      }

      if (this.options.allowResize && this.options.allowMove) {
        this._initResizable();
        this._initMovable();
      } else if (this.options.allowResize && !this.options.allowMove) {
        this._initResizable();
      } else if (!this.options.allowResize && this.options.allowMove) {
        this._initMovable();
      }

      if (this.options.header && !this._movable) {
        this.header.$el.css("cursor", "default");
      } else {
        this.header.$el.css("cursor", "");
      }
    },
    /**
     * Removes all controls from the control bars.
     */
    clearControls: function () {
      this.header.getRightControlBar().children().not(".window-keep").remove();
      this.header.getLeftControlBar().children().remove();
    },
    /**
     * Gets a value indicating whether or not the window is visible.
     * @return {Boolean} True if the window is visible, otherwise false.
     */
    isVisible: function () {
      return this._visible;
    },
    /**
     * Gets a value indicating whether or not the window is maximized.
     * @return {boolean} True if the window is maximized, otherwise false.
     */
    isMaximized: function () {
      return this._maximized;
    },
    /**
     * Sets the windowView to not be visible
     */
    hide: function () {
      this.$el.hide();
      this._visible = false;
      this.trigger("hidden");
    },
    /**
     * Shows the windowView by setting it to be visible
     */
    show: function () {
      this.$el.show();

      if (!this._maximized) {
        this._resetDimensions();
      }
      this._visible = true;
      this.trigger("shown");
    },
    /**
     * Sets the WindowView to either be visible or not based on its current value
     */
    toggle: function () {
      if (this._visible) {
        this.hide();
      } else {
        this.show();
        this.bringToFront();
      }
    },
    /**
     * Refreshes the WindowView.
     */
    refresh: function () {
      this._resetDimensions();
    },
    /**
     * Center the window in its container
     */
    center: function () {
      //center the window in its container
      var containerDimensions = this._containerDimensions();
      var dimensions = _.extend({}, this.size(), this.position());

      this.position({
        top:
          containerDimensions.top +
          (containerDimensions.height - dimensions.height) / 2,
        left:
          containerDimensions.left +
          (containerDimensions.width - dimensions.width) / 2,
      });
    },
    /**
     * Position the top left of the WindowView to the coordinates specified
     * @param {Object} coords - the coordinates to position the windowView to
     */
    position: function (coords) {
      if (coords) {
        this._coords = coords;
        this.$el.offset(coords);
      }

      return this._coords || this.$el.offset();
    },

    /**
     * Size the WindowView to the sizes specified
     * @param {Object} width - either an object containing both the width and height to be sized to or a number containing the width
     * @param {Number} width - either an object containing both the width and height to be sized to or a number containing the width
     * @param {Number} height -  a number containing the height for resizing the element
     */
    size: function (width, height) {
      var trigger = false;

      if (_.isObject(width)) {
        //width and height are in an object
        height = width.height;
        width = width.width;
      }

      if (_.isNumber(width)) {
        //width = Math.max(width, WindowView.MIN_WIDTH);
        this.$el.width(width);
        trigger = true;
      }

      if (_.isNumber(height)) {
        //height = Math.max(height, WindowView.MIN_HEIGHT);
        //zthis.$('.panel-body').height(height - footerHeight - headerHeight - 60);
        this.$el.height(height);
        trigger = true;
      }

      if (trigger) {
        this._recalculateContentSize(); //only call this when updating size, not when getting it, cuz this calls size to get the size as well
      }

      var size = {
        width: this.$el.width(),
        height: this.$el.height(),
      };

      return size;
    },
    /**
     * Set The content of the windowView
     * @param {String} content - the content of the window
     */
    setContent: function (content) {
      if (content != this._content) {
        var body = this.$("> .panel-body");

        body.empty();

        while (body.firstChild) {
          body.removeChild(body.firstChild);
        }
        this._content = content;

        if (_.isString(content)) {
          body.html(content);
        } else if (_.isObject(content)) {
          content.appendTo(body);
        }
        body.show();
      }
    },
    /**
     * Add additional content to the windowView
     * @param {String} content - the content to append to the window
     */
    appendContent: function (content) {
      this._content = content;
      this.$("> .panel-body").append(content);
    },
    /**
     * Add additional content to the windowView before the current content
     * @param {String} content - the content to prepend to the window
     */
    prependContent: function (content) {
      this._content = content;
      this.$("> .panel-body").prepend(content);
    },
    /**
     * Set The content of the footer of the windowView
     * @param content - the content of the footer
     */
    setFooter: function (content) {
      this._footer = content;
      var footer = this.$("> .panel-footer");

      footer.empty();

      while (footer.firstChild) {
        footer.removeChild(footer.firstChild);
      }

      if (_.isString(content)) {
        footer.html(content);
      } else if (_.isObject(content)) {
        content.appendTo(footer);
      }

      footer.show();

      if (this._anchor && this._anchor.bottom) {
        this._resetDimensions(); //reset dimensions to accomodate the footer
      } else {
        this._recalculateContentSize();
      }
    },

    /**
     * Recalculates the size of the footer and adjusts the size of the filler as needed.
     */
    _recalculateContentSize: function () {
      var size = this.size();
      var header = this.$("> .panel-heading");
      var footer = this.$("> .panel-footer");

      this.$("> .panel-body").height(
        size.height - header.outerHeight() - footer.outerHeight()
      );
    },

    /**
     * Set The content for the header of the windowView
     * @param {String} content - the content of the header
     * @param {Boolean} allowClose - allow the window to be closed
     * @param {Boolean} allowResize - allow the window to be resized
     * @param {Boolean} allowMaximize - allow the window to be maximized
     * @param {Boolean} allowMove - allow the window to be moved
     */
    setHeader: function (
      content,
      allowClose,
      allowResize,
      allowMaximize,
      allowMove
    ) {
      this.header = new PanelHeadingView({
        title: content,
      });
      this.header.render();
      this.$("> .panel-heading").remove();
      this.$("> .panel-body").before(this.header.$el);

      if (allowClose) {
        this.header
          .getRightControlBar()
          .append(
            '<button class="close window-keep"><span>&times;</span></button>'
          );
      }

      if (allowMaximize) {
        this.header
          .getRightControlBar()
          .append(
            '<span class="window-keep glyphicon glyphicon-resize-full size-control btn btn-default"></span>'
          );
      }

      if (allowResize && allowMove) {
        this._initResizable();
        this._initMovable();
      } else if (allowResize && !allowMove) {
        this._initResizable();
      } else if (!allowResize && allowMove) {
        this._initMovable();
      }

      if (!this._movable) {
        this.header.$el.css("cursor", "default");
      } else {
        this.header.$el.css("cursor", "");
      }
    },
    /**
     * Get The content of the windowView
     * @returns {String} - The content of the windowView
     */
    getContent: function () {
      return this._content;
    },
    /**
     * Get The footer of the windowView
     * @returns {String} - The content of the footer of the windowView
     */
    getFooter: function () {
      return this._footer;
    },
    /**
     * Maximizes the windowView
     */
    maximize: function () {
      if (!this._maximized) {
        this.trigger("maximizing");
        //save the WindowView Size
        this._oldSize = this.size();
        this._oldPos = this.position();
        //first maximize the outside div
        var zIndex = this.options["z-index"];

        if (zIndex != 1) {
          //do not change the z-index on maximize if it is the background map window
          zIndex = this.getMaxZ() + 1;
        }
        this.$el.css({
          position: "fixed",
          left: 0,
          top: page.getHeaderHeight(),
          width: $("body > .scroll-container").width(),
          height: page.getMaxHeight(),
          padding: 0,
          "z-index": zIndex,
        });
        //disable dragging on maximized windows
        if (this.options.allowMove) {
          this.$el.draggable("disable");
        }
        this.$(".size-control").toggleClass(
          "glyphicon-resize-full glyphicon-resize-small"
        );
        this._recalculateContentSize();
        this._maximized = true;
        this.trigger("maximized");
      }
    },
    /**
     * Brings The window to the front of the screen
     */
    bringToFront: function () {
      this.setZIndex(this.getMaxZ() + 1); //set the current windows z-index above the others
    },

    /**
     *Returns the Maximum zIndex for the window Views
     */
    getMaxZ: function () {
      var windowList = $(".window.well");

      if (!_.isEmpty(windowList)) {
        return _.max(windowList, function (el) {
          return Number($(el).css("z-index"));
        });
      } else {
        return 0;
      }
    },

    /**
     * Sets the z-index property on the windowView
     */
    setZIndex: function (value) {
      this.$el.css({
        position: "absolute",
        "z-index": value,
      });
    },
    /**
     * Restores the windowView size to its previous size before it was maximized
     */
    restore: function () {
      if (this._maximized) {
        this.trigger("restoring");
        // this.$el.css({
        // 	position: 'absolute',
        // 	padding: ''
        // });

        //use the WindowView Size
        this.$(".size-control").toggleClass(
          "glyphicon-resize-full glyphicon-resize-small"
        );
        this._maximized = false;
        this._resetDimensions(this._oldSize, this._oldPos);

        if (this.options.allowMove) {
          this.$el.draggable("enable");
        }
        this.trigger("restored");
      }
    },
    /**
     * Clears the ability to restore
     */
    _clearRestore: function () {
      // this.$el.css({
      // 	position: 'absolute',
      // 	padding: '',
      // 	'z-index': ''
      // });
      this.$(".size-control").toggleClass(
        "glyphicon-resize-full glyphicon-resize-small"
      );
      this._maximized = false;
      this.trigger("restored");
    },

    /**
     * Removes the view from the DOM and unbinds all events.
     */
    remove: function () {
      this.unbind();
      this.$el.remove();
      this.trigger("destroy");
    },

    /**
     * Creates a container where the windowView is placed
     * @returns {Object} - The container with all the demensions of the container as properties
     */
    _containerDimensions: function () {
      //add and subtract the main window header height pixels to accomodate the header height of the main window
      var headerHeight = WindowView.getHeaderHeight(); //using mapView.$el is causing issues in IE
      var container = {
        left: 0,
        top: page.getHeaderHeight(), // + headerHeight,
        width: page.getScrollWidth(),
        height: page.getMaxHeight(), // - headerHeight
      };

      if (this._anchor && this._anchor.container) {
        var cont = $(this._anchor.container);

        container = {
          left: cont.offset().left,
          top: cont.offset().top + headerHeight,
          width: cont.width(),
          height: cont.height() - headerHeight,
        };
      }

      return container;
    },
    /**
     * Creates a shadow behind the window
     * @param {boolean} shadow - determines if the window should have a shadow or the shadow should be removed
     */
    setShadow: function (shadow) {
      if (shadow) {
        //this.$el.addClass('shadow');
      } else {
        //this.$el.removeClass('shadow');
      }
    },
    /**
     * Resizes the WindowView, if it uses a container, this function will place the WindowView within that container
     * @param {object} size - determines the size of the window to restore to
     * @param {object} pos - determines the position of the window to restore to
     */
    _resetDimensions: function (size, pos) {
      //get WindowView dimensions
      if (_.isUndefined(size)) {
        size = this.size();
      }

      if (_.isUndefined(pos)) {
        pos = this.position();
      }
      size.width = Math.max(WindowView.MIN_WIDTH, size.width);
      size.height = Math.max(WindowView.MIN_HEIGHT, size.height);

      var container = this._containerDimensions();
      var anchor = this._anchor;

      if (this._maximized) {
        //if maximized, take the size of our container regardless
        anchor = {
          top: true,
          bottom: true,
          left: true,
          right: true,
        };
      }

      if (anchor) {
        if (anchor.top) {
          pos.top = container.top;

          if (anchor.bottom) {
            size.height = container.height;
          }
        } else if (anchor.bottom) {
          pos.top = container.top + container.height - size.height;
        }

        if (anchor.left) {
          pos.left = container.left;

          if (anchor.right) {
            size.width = container.width;
          }
        } else if (anchor.right) {
          pos.left = container.left + container.width - size.width;
        }
      }

      this.size(size);
      this.position(pos);
    },

    /**
     * @private
     * Value for remembering whether scrolling was enabled before a window was maximized.
     */
    _oldScroll: null,
  },
  {
    MAX_WIDTH: 3000,
    MAX_HEIGHT: 3000,
    MIN_WIDTH: 250,
    MIN_HEIGHT: 250,

    windows: [],
    events: _.extend({}, Backbone.Events),
    maximizedCount: 0,

    /**
     * Creates a WindowView and registers it with the application.
     * @param  {string} title   The title of the window.
     * @param  {boolean} [show=false]    True to show the window immediately, otherwise false.
     * @param  {object} options Options to send to the WindowView constructor.
     * @param options.content The content to place in the WindowView once it is created.
     * @param options.footer The content to place in the WindowView footer once it is created.
     * @return {WindowView}         The WindowView that was created.
     */
    create: function (title, show, options) {
      if (_.isObject(show)) {
        options = show;
        show = false;
      }

      var opts = _.defaults({}, options, {
        title: title,
      });
      var windowView = new WindowView(opts);

      windowView.render();

      $("body").append(windowView.$el);

      if (opts.content) {
        windowView.setContent(opts.content);
      }

      if (opts.footer && opts.footer !== true) {
        windowView.setFooter(opts.footer);
      }

      if (show) {
        windowView.show();
      }

      return WindowView.register(windowView);
    },

    /**
     * Registers a WindowView with the application.
     * @param  {WindowView} windowView The WindowView to register.
     * @return {WindowView}            The WindowView that was registered.
     */
    register: function (windowView) {
      WindowView.windows.push(windowView);
      windowView.on(
        "destroy",
        function () {
          var idx = WindowView.windows.indexOf(windowView);

          if (idx !== -1) {
            WindowView.windows.splice(idx, 1);
          }

          if (windowView._maximized) {
            onRestore();
          }

          windowView.off("maximizing");
          windowView.off("restoring");
        },
        this
      );
      windowView.on("maximizing", onMaximize);
      windowView.on("restoring", onRestore);

      return windowView;
    },

    /**
     * Gets the height of each window header, based on the first header on the page.
     */
    getHeaderHeight: function () {
      return $($(".panel-heading")[0]).outerHeight();
    },
  }
);

function onRestore() {
  if (WindowView.maximizedCount > 0) {
    WindowView.maximizedCount--;

    if (WindowView.maximizedCount == 0) {
      WindowView.events.trigger("expose");
    }
  }
}

function onMaximize() {
  WindowView.maximizedCount++;

  if (WindowView.maximizedCount == 1) {
    //one was just maximized
    WindowView.events.trigger("obscure");
  }
}

WindowView.events = _.extend({}, Backbone.Events);

export default WindowView;



//////////////////
// WEBPACK FOOTER
// ./public/js/core/views/WindowView.js
// module id = 86
// module chunks = 1